import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Atom, KeyRound, LogOut, ShieldCheck } from "lucide-react";

export default function Navbar() {
    const { user, logout } = useAuth();
    const loc = useLocation();
    const nav = useNavigate();

    const isActive = (p) => loc.pathname === p || loc.pathname.startsWith(p + "/");

    const links = user?.role === "admin"
        ? [
            { to: "/admin", label: "Overview" },
            { to: "/admin/providers", label: "LLM Providers" },
            { to: "/admin/settings", label: "Settings" },
            { to: "/admin/workers", label: "Workers" },
            { to: "/admin/bank", label: "Vector DB" },
        ]
        : user ? [
            { to: "/dashboard", label: "Dashboard" },
            { to: "/generate", label: "Generate" },
            { to: "/history", label: "History" },
            { to: "/ai-settings", label: "AI Keys" },
            { to: "/subscription", label: "Subscription" },
            { to: "/desktop", label: "Desktop Worker" },
        ] : [
            { to: "/", label: "Home" },
            { to: "/desktop", label: "Contribute" },
        ];

    return (
        <header className="sticky top-0 z-40 w-full border-b border-border bg-background/95 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
                <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2 group">
                    <div className="relative w-8 h-8 rounded-sm bg-primary flex items-center justify-center btn-press group-hover:rotate-12 transition-transform">
                        <Atom className="w-5 h-5 text-primary-foreground" strokeWidth={2.5} />
                    </div>
                    <div className="flex flex-col leading-none">
                        <span className="font-heading font-black text-lg tracking-tight">
                            Prepcheck
                        </span>
                        <span className="overline text-[9px] text-muted-foreground -mt-0.5">Illume AI Innovations</span>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    {links.map((l) => (
                        <Link key={l.to} to={l.to} data-testid={`nav-link-${l.to.replace(/\//g, '-')}`}
                            className={`px-3 py-1.5 text-sm font-medium rounded-sm btn-press hover:bg-accent transition-colors ${isActive(l.to) ? "bg-accent text-foreground" : "text-muted-foreground"}`}>
                            {l.label}
                        </Link>
                    ))}
                </nav>

                <div className="flex items-center gap-2">
                    {user ? (
                        <>
                            <div className="hidden sm:flex flex-col items-end leading-tight">
                                <span className="text-sm font-medium">{user.full_name}</span>
                                <span className="overline text-[9px] text-muted-foreground flex items-center gap-1">
                                    {user.role === "admin" && <ShieldCheck className="w-3 h-3 text-primary" />}
                                    {user.role}
                                </span>
                            </div>
                            {user.role !== "admin" && (
                                <Button data-testid="nav-ai-settings-btn" variant="ghost" size="sm"
                                    className="btn-press rounded-sm hidden lg:inline-flex"
                                    onClick={() => nav("/ai-settings")}>
                                    <KeyRound className="w-4 h-4 mr-1" /> AI
                                </Button>
                            )}
                            <Button data-testid="nav-logout-btn" variant="outline" size="sm"
                                className="btn-press rounded-sm"
                                onClick={() => { logout(); nav("/"); }}>
                                <LogOut className="w-4 h-4 mr-1" /> Logout
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button data-testid="nav-login-btn" variant="ghost" size="sm" className="rounded-sm" onClick={() => nav("/login")}>Login</Button>
                            <Button data-testid="nav-register-btn" size="sm" className="rounded-sm bg-primary hover:bg-primary/90 btn-press" onClick={() => nav("/register")}>
                                Register free
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </header>
    );
}
